<div class="flex flex-col gap-6">
    @if (!embedded) {
    <!-- Toujours dans un panneau ou une feuille : le parent gère la zone sûre et les marges.
         Intégré dans l'assistant « Nouvelle zone », l'en-tête est celui de l'assistant. -->
    <div class="flex flex-col">
        <vw-page-header [safeArea]="false" [inset]="true" [backLabel]="backLabel"
            [title]="location?.name || ('profile.addresses.edit-address.create-location.title' | translate)"
            (back)="onCloseLocationForm()"></vw-page-header>
    </div>

    <!-- Encart explicatif : zone (espace) vs planche -->
    <div class="flex items-start gap-2 rounded-card bg-vw-surface-tint p-3 text-body-s text-vw-primary-ink">
        <vw-location-home-icon class="mt-0.5 block h-5 w-5 shrink-0 [&>svg]:h-full [&>svg]:w-full" aria-hidden="true" />
        <p>{{ 'profile.addresses.edit-address.edit-locations.intro' | translate }}</p>
    </div>
    }

    <!-- Nom (obligatoire) -->
    <div class="flex flex-col gap-2">
        <label for="zone-name" class="text-body-s font-semibold text-vw-body">{{
            'profile.addresses.edit-address.edit-locations.name.label' | translate }} *</label>
        <vw-text-input inputId="zone-name" [(value)]="name" (valueChange)="onNameChange($event)"
            [isError]="submitAttempted && !isNameValid" data-test="zone-name"></vw-text-input>
        @if (submitAttempted && !isNameValid) {
            <p role="alert" class="text-body-s text-vw-danger" data-test="zone-name-error">{{ 'profile.addresses.edit-address.edit-locations.name.error' | translate }}</p>
        } @else {
            <p class="text-body-s text-vw-muted">{{ 'profile.addresses.edit-address.edit-locations.name.placeholder' | translate }}</p>
        }
    </div>

    <!-- Type de zone : cartes illustrées (sans « Bac potager ») -->
    <div class="flex flex-col gap-2">
        <p class="text-body-s font-semibold text-vw-body">{{
            'profile.addresses.edit-address.edit-locations.type.label' | translate }}</p>
        <vw-choice-selector [ariaLabel]="('profile.addresses.edit-address.edit-locations.type.label') | translate" [options]="typeOptions" [(value)]="selectedType" variant="cards" [columns]="3"></vw-choice-selector>
    </div>

    <!-- Exposition : la même liste que pour une planche (Ombre, Mi-ombre, Ensoleillé, Plein soleil). -->
    <div class="flex flex-col gap-2">
        <p class="text-body-s font-semibold text-vw-body">{{
            'profile.addresses.edit-address.edit-locations.sunshine.label' | translate }}</p>
        <vw-choice-selector [ariaLabel]="('profile.addresses.edit-address.edit-locations.sunshine.label') | translate" [options]="sunshineOptions" [(value)]="selectedSunshine" variant="chips"></vw-choice-selector>
        <p class="text-body-s text-vw-muted">{{ 'profile.addresses.edit-address.edit-locations.sunshine.help' | translate }}</p>
    </div>

    <!-- Dimensions (optionnel) → surface dérivée + futures suggestions de planches -->
    <div class="flex flex-col gap-2">
        <div class="flex items-center justify-between">
            <p class="text-body-s font-semibold text-vw-body">{{
                'profile.addresses.edit-address.edit-locations.dimensions.label' | translate }}</p>
            <span class="rounded-pill bg-vw-surface-sunk px-2 py-0.5 text-caption text-vw-muted">{{
                'profile.addresses.edit-address.edit-locations.dimensions.optional' | translate }}</span>
        </div>
        <div class="flex items-end gap-3">
            <label class="flex-1">
                <span class="mb-1 block text-caption text-vw-muted">{{
                    'profile.addresses.edit-address.edit-locations.dimensions.width' | translate }}</span>
                <span class="relative block">
                    <input type="number" inputmode="decimal" min="0.1" step="0.1" [(ngModel)]="width" data-test="zone-width"
                        class="min-h-btn w-full rounded-field border border-vw-line-strong bg-vw-surface px-3 pr-8 text-body text-vw-ink outline-none focus:border-vw-primary" />
                    <span class="absolute right-3 top-1/2 -translate-y-1/2 text-body-s text-vw-muted">m</span>
                </span>
            </label>
            <span class="pb-3 text-body text-vw-muted" aria-hidden="true">×</span>
            <label class="flex-1">
                <span class="mb-1 block text-caption text-vw-muted">{{
                    'profile.addresses.edit-address.edit-locations.dimensions.length' | translate }}</span>
                <span class="relative block">
                    <input type="number" inputmode="decimal" min="0.1" step="0.1" [(ngModel)]="length" data-test="zone-length"
                        class="min-h-btn w-full rounded-field border border-vw-line-strong bg-vw-surface px-3 pr-8 text-body text-vw-ink outline-none focus:border-vw-primary" />
                    <span class="absolute right-3 top-1/2 -translate-y-1/2 text-body-s text-vw-muted">m</span>
                </span>
            </label>
        </div>
        @if (derivedSurface) {
            <!-- La surface sous les deux champs : plus de libellé décalé d'une ligne. -->
            <p class="text-body-s text-vw-primary-ink" data-test="zone-surface">
                {{ 'profile.addresses.edit-address.edit-locations.dimensions.surface' | translate }} : ≈ {{ derivedSurface }} m²
            </p>
        }
        <p class="text-body-s text-vw-muted">{{ 'profile.addresses.edit-address.edit-locations.dimensions.help' | translate }}</p>
    </div>

    @if (showDelete && location) {
        <button type="button" (click)="onDeleteLocation()" data-test="zone-delete"
            class="mx-auto min-h-target px-3 text-body-s font-semibold text-vw-danger">
            {{ 'profile.addresses.edit-address.delete-zone' | translate }}
        </button>
    }

    <!-- Barre d'action collante, comme la création de planche : le bouton reste sous le pouce,
         jamais grisé (un envoi sans nom dit ce qui manque) ; l'encart Premium et l'erreur au-dessus.
         Elle colle au bas de ce qui défile : l'hôte ne met donc PAS de marge sous le formulaire
         (elle laisserait voir le formulaire sous la barre). La barre porte la zone sûre du bas,
         sauf dans une feuille, dont le corps la réserve déjà (voir le .scss). -->
    <div class="zone-action-bar sticky bottom-0 z-10 -mx-4 flex flex-col gap-3 border-t border-vw-line bg-vw-surface px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]"
        data-test="zone-action-bar">
        @if (premiumMessageKey) {
            <!-- Refus premium : invitation en contexte, jamais une erreur rouge -->
            <vw-premium-teaser
                [compact]="true"
                [message]="premiumMessageKey | translate"
                [detail]="'premium.teaser.zones-detail' | translate"
                [source]="'zone_limit'"
                (ctaClicked)="openPaywall()"
            />
        }
        @if (saveFailed) {
            <p role="alert" class="text-body-s text-vw-danger" data-test="zone-save-error">{{ 'profile.addresses.edit-address.edit-locations.save-error' | translate }}</p>
        }
        <vw-button data-test="zone-submit" look="primary" customClass="w-full" [loading]="isSubmittingLocation"
            [label]="embedded ? 'garden.zone-wizard.continue' : 'profile.addresses.edit-address.button'"
            (onClick)="onSubmitLocationForm()"></vw-button>
    </div>
</div>

<vw-premium-toast
  [isOpen]="premiumPaywallOpen"
  [source]="'zone_limit'"
  (close)="premiumPaywallOpen = false"
></vw-premium-toast>
